import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
  Tooltip,
  Alert,
  CircularProgress,
  TablePagination,
  Link,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Refresh,
  AccountBalance,
  Business,
  AccessTime,
  Warning,
  Replay,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { hostPayoutsService, type HostPayout } from '@/services/hostPayoutsService';

type StatusFilter = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'all';

export function HostPayoutsTab() {
  const [activeTab, setActiveTab] = useState<StatusFilter>('pending');
  const [payouts, setPayouts] = useState<HostPayout[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPayout, setSelectedPayout] = useState<HostPayout | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [retryNotes, setRetryNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0,
    all: 0,
  });

  useEffect(() => {
    loadPayouts();
  }, [activeTab, page, rowsPerPage]);

  // Load counts for all statuses
  useEffect(() => {
    loadStatusCounts();
  }, []);

  // Auto-refresh every 30 seconds for processing payouts
  useEffect(() => {
    if (activeTab === 'processing') {
      const interval = setInterval(() => {
        loadPayouts();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  const loadStatusCounts = async () => {
    try {
      // Fetch counts for each status in parallel
      const [pendingRes, processingRes, completedRes, failedRes, allRes] = await Promise.all([
        hostPayoutsService.getPayouts({ status: 'pending', limit: 1, offset: 0 }),
        hostPayoutsService.getPayouts({ status: 'processing', limit: 1, offset: 0 }),
        hostPayoutsService.getPayouts({ status: 'completed', limit: 1, offset: 0 }),
        hostPayoutsService.getPayouts({ status: 'failed', limit: 1, offset: 0 }),
        hostPayoutsService.getPayouts({ limit: 1, offset: 0 }),
      ]);

      setStatusCounts({
        pending: pendingRes.pagination?.total || 0,
        processing: processingRes.pagination?.total || 0,
        completed: completedRes.pagination?.total || 0,
        failed: failedRes.pagination?.total || 0,
        all: allRes.pagination?.total || 0,
      });
    } catch (error) {
      console.error('Failed to load status counts:', error);
    }
  };

  const loadPayouts = async () => {
    setIsLoading(true);
    try {
      console.log('Loading host payouts with params:', {
        status: activeTab === 'all' ? undefined : activeTab,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });

      const response = await hostPayoutsService.getPayouts({
        status: activeTab === 'all' ? undefined : activeTab,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });

      console.log('Host payouts response:', response);

      if (response && response.payouts) {
        console.log('Setting payouts:', response.payouts.length, 'items');
        console.log('First payout sample:', response.payouts[0]);
        console.log('Host data structure:', response.payouts[0]?.host);
        setPayouts(response.payouts);
        setTotalCount(response.pagination?.total || 0);
      } else {
        console.warn('No payouts in response or invalid response structure:', response);
        setPayouts([]);
        setTotalCount(0);
      }
    } catch (error: any) {
      console.error('Failed to load host payouts:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      const errorMsg = error.response?.data?.message || error.message || 'Failed to load host payouts';
      toast.error(errorMsg);
      setPayouts([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedPayout) return;

    setIsProcessing(true);
    try {
      await hostPayoutsService.approvePayout(selectedPayout.id);
      toast.success('Host payout approved and transfer initiated successfully! ✅');
      setShowApproveModal(false);
      setSelectedPayout(null);
      loadPayouts();
      loadStatusCounts(); // Refresh counts after approval
    } catch (error: any) {
      console.error('Failed to approve host payout:', error);
      toast.error(error.response?.data?.message || 'Failed to approve host payout');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedPayout) return;

    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setIsProcessing(true);
    try {
      await hostPayoutsService.rejectPayout(selectedPayout.id, rejectReason);
      toast.success('Host payout rejected');
      setShowRejectModal(false);
      setSelectedPayout(null);
      setRejectReason('');
      loadPayouts();
      loadStatusCounts(); // Refresh counts after rejection
    } catch (error: any) {
      console.error('Failed to reject host payout:', error);
      toast.error(error.response?.data?.message || 'Failed to reject host payout');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetry = async () => {
    if (!selectedPayout) return;

    setIsProcessing(true);
    try {
      const response = await hostPayoutsService.retryPayout(selectedPayout.id, retryNotes.trim());

      if (response.success) {
        toast.success(response.message || 'Payout retry initiated successfully! ✅');
      }

      setShowRetryModal(false);
      setSelectedPayout(null);
      setRetryNotes('');
      loadPayouts();
      loadStatusCounts(); // Refresh counts after retry
    } catch (error: any) {
      console.error('Failed to retry host payout:', error);
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || 'Failed to retry host payout';
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'completed': return 'success';
      case 'failed':
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => status.charAt(0).toUpperCase() + status.slice(1);
  const formatCurrency = (amount: number) => `₦${amount.toLocaleString()}`;
  const formatDate = (dateString: string) => new Date(dateString).toLocaleString('en-NG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Host Payouts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Approve or reject host withdrawal requests via Paystack
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadPayouts} disabled={isLoading}>
          Refresh
        </Button>
      </Box>

      {activeTab === 'pending' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Important:</strong> Approving a payout will immediately transfer money to the host's
            bank account via Paystack. This action cannot be undone.
          </Typography>
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab label={`Pending (${statusCounts.pending})`} value="pending" icon={<AccessTime />} iconPosition="start" />
              <Tab label={`Processing (${statusCounts.processing})`} value="processing" icon={<Refresh />} iconPosition="start" />
              <Tab label={`Completed (${statusCounts.completed})`} value="completed" icon={<CheckCircle />} iconPosition="start" />
              <Tab label={`Failed (${statusCounts.failed})`} value="failed" icon={<Warning />} iconPosition="start" />
              <Tab label={`All (${statusCounts.all})`} value="all" />
            </Tabs>
          </Box>

          <TableContainer>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : payouts.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 6 }}>
                <AccountBalance sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">No host payouts found</Typography>
                <Typography variant="body2" color="text.secondary">
                  No {activeTab !== 'all' && activeTab} host payouts match your current filter.
                </Typography>
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Payout ID</TableCell>
                    <TableCell>Host</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Bank Account</TableCell>
                    <TableCell>Requested</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payouts.map((payout) => (
                    <TableRow key={payout.id} hover>
                      <TableCell>
                        <Tooltip title={payout.id}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '11px' }}>
                            {payout.id.substring(0, 8)}...
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                            <Business sx={{ fontSize: 18 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {payout.host?.name ||
                               (payout.host?.firstName && payout.host?.lastName
                                 ? `${payout.host.firstName} ${payout.host.lastName}`
                                 : 'N/A')}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {payout.host?.email || payout.host?.code || ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {formatCurrency(payout.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{payout.bankAccount.accountName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {payout.bankAccount.accountNumber} • {payout.bankAccount.bankName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(payout.requestedAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={getStatusLabel(payout.status)} color={getStatusColor(payout.status) as any} size="small" />
                          {payout.status === 'failed' && payout.retryCount !== undefined && payout.retryCount > 0 && (
                            <Chip
                              label={`${payout.retryCount} ${payout.retryCount === 1 ? 'retry' : 'retries'}`}
                              size="small"
                              variant="outlined"
                              color="warning"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {payout.status === 'pending' && (
                            <>
                              <Tooltip title="Approve payout">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => {
                                    setSelectedPayout(payout);
                                    setShowApproveModal(true);
                                  }}
                                >
                                  <CheckCircle />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject payout">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setSelectedPayout(payout);
                                    setShowRejectModal(true);
                                  }}
                                >
                                  <Cancel />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {payout.status === 'failed' && (
                            <>
                              <Tooltip title={`Retry payout${payout.retryCount ? ` (${payout.retryCount} previous attempts)` : ''}`}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setSelectedPayout(payout);
                                    setShowRetryModal(true);
                                  }}
                                >
                                  <Replay />
                                </IconButton>
                              </Tooltip>
                              {payout.failedReason && (
                                <Tooltip title={payout.failedReason}>
                                  <IconButton size="small">
                                    <Warning color="error" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          {!isLoading && payouts.length > 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          )}
        </CardContent>
      </Card>

      {/* Approve Modal */}
      <Dialog
        open={showApproveModal}
        onClose={() => !isProcessing && setShowApproveModal(false)}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" />
            Approve Host Payout
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPayout && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will immediately transfer money to the host's bank account via Paystack. This action cannot be undone.
              </Alert>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>Host</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  {selectedPayout.host?.name ||
                   (selectedPayout.host?.firstName && selectedPayout.host?.lastName
                     ? `${selectedPayout.host.firstName} ${selectedPayout.host.lastName}`
                     : 'Unknown Host')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedPayout.host?.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>Amount</Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                  {formatCurrency(selectedPayout.amount)}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>Bank Account</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>{selectedPayout.bankAccount.accountName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedPayout.bankAccount.accountNumber} • {selectedPayout.bankAccount.bankName}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApproveModal(false)} disabled={isProcessing}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            {isProcessing ? 'Processing...' : 'Approve & Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Modal */}
      <Dialog
        open={showRejectModal}
        onClose={() => !isProcessing && setShowRejectModal(false)}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Cancel color="error" />
            Reject Host Payout
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPayout && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Rejecting this payout will cancel the request and notify the host.
              </Alert>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>Host</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  {selectedPayout.host?.name ||
                   (selectedPayout.host?.firstName && selectedPayout.host?.lastName
                     ? `${selectedPayout.host.firstName} ${selectedPayout.host.lastName}`
                     : 'Unknown Host')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedPayout.host?.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>Amount</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  {formatCurrency(selectedPayout.amount)}
                </Typography>
              </Box>
              <TextField
                fullWidth
                label="Rejection Reason"
                multiline
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Provide a reason for rejecting this payout..."
                required
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectModal(false)} disabled={isProcessing}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={isProcessing || !rejectReason.trim()}
            startIcon={isProcessing ? <CircularProgress size={16} /> : <Cancel />}
          >
            {isProcessing ? 'Processing...' : 'Reject Payout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retry Modal */}
      <Dialog
        open={showRetryModal}
        onClose={() => !isProcessing && setShowRetryModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Replay color="primary" />
            Retry Failed Payout
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPayout && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                This will retry the failed payout by attempting to transfer the money again via Paystack.
                {selectedPayout.retryCount !== undefined && selectedPayout.retryCount > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <strong>Note:</strong> This payout has already been retried {selectedPayout.retryCount} time{selectedPayout.retryCount > 1 ? 's' : ''}.
                    Maximum retry limit is 5 attempts.
                  </Box>
                )}
              </Alert>

              {selectedPayout.failedReason && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Previous Failure Reason:</Typography>
                  <Typography variant="body2">{selectedPayout.failedReason}</Typography>
                </Alert>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>Host</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                  {selectedPayout.host?.name ||
                   (selectedPayout.host?.firstName && selectedPayout.host?.lastName
                     ? `${selectedPayout.host.firstName} ${selectedPayout.host.lastName}`
                     : 'Unknown Host')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedPayout.host?.email}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>Amount</Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  {formatCurrency(selectedPayout.amount)}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>Bank Account</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>{selectedPayout.bankAccount.accountName}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedPayout.bankAccount.accountNumber} • {selectedPayout.bankAccount.bankName}
                </Typography>

                {selectedPayout.retryCount !== undefined && selectedPayout.retryCount > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Retry Count</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedPayout.retryCount} / 5 attempts
                    </Typography>
                  </>
                )}
              </Box>

              <TextField
                fullWidth
                label="Admin Notes (Optional)"
                multiline
                rows={3}
                value={retryNotes}
                onChange={(e) => setRetryNotes(e.target.value)}
                placeholder="e.g., Retrying after funding Paystack account..."
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRetryModal(false)} disabled={isProcessing}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRetry}
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={16} /> : <Replay />}
          >
            {isProcessing ? 'Retrying...' : 'Retry Payout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
